import React, { useState } from 'react';
import DsaExplanationModal from './dsa/DsaExplanationModal';
import DsaProblemModal from './dsa/DsaProblemModal';
import EXPLANATIONS from '../data/dsaExplanations';
import PROBLEMS from '../data/dsaProblems';
import API, { downloadFile } from '../utils/api';

const DIFFICULTY_COLOR = { Easy: '#2ECC71', Medium: '#E8834C', Hard: '#F04155' };

const V1 = '#7F00FF', V2 = '#B163FF';

// Structural metadata only — pattern names + how many problems each will hold.
// No problem content/explanations here on purpose.
const PATTERN_GROUPS = [
  { name: 'Arrays', count: 3 },
  { name: 'Hashing', count: 4 },
  { name: 'Two Pointers', count: 3 },
  { name: 'Sliding Window', count: 4 },
  { name: 'Stack', count: 1 },
  { name: 'Binary Search', count: 2 },
  { name: 'Linked List', count: 6 },
  { name: 'Trees', count: 11 },
  { name: 'Tries', count: 3 },
  { name: 'Heap / Priority Queue', count: 2 },
  { name: 'Backtracking', count: 2 },
  { name: 'Graphs', count: 6 },
  { name: 'Advanced Graphs', count: 1 },
  { name: '1-D Dynamic Programming', count: 9 },
  { name: '2-D Dynamic Programming', count: 2 },
  { name: 'Greedy', count: 1 },
  { name: 'Intervals', count: 5 },
  { name: 'Math & Bit Manipulation', count: 5 },
  { name: 'Matrix', count: 3 },
  { name: 'Palindromes', count: 2 },
];

const TOTAL_PROBLEMS = PATTERN_GROUPS.reduce((sum, g) => sum + g.count, 0);

// Flat list of every curated problem across every pattern, used to compute
// the Easy/Medium/Hard solved breakdown shown in the progress hero.
const ALL_PROBLEMS = PATTERN_GROUPS.flatMap(g => PROBLEMS[g.name] || []);

const DIFFICULTY_FILTERS = ['All', 'Easy', 'Medium', 'Hard'];

const ChevronIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', flexShrink: 0 }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const BookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const LockIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const CertificateIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6" /><path d="M8.5 13.5 7 22l5-3 5 3-1.5-8.5" />
  </svg>
);

/* ─── Progress Hero ───────────────────────────────────────── */
const ProgressHero = ({ solved, total, breakdown }) => {
  const pct = total === 0 ? 0 : Math.round((solved / total) * 100);
  return (
    <div style={{
      background: `linear-gradient(135deg, #2B0057, #51158C)`,
      borderRadius: 22, padding: '32px 36px', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 24, flexWrap: 'wrap',
    }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 8 }}>
          Your Progress
        </div>
        <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 'clamp(28px,4vw,38px)' }}>
          {solved} <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.55em' }}>/ {total} solved</span>
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 6 }}>
          {breakdown.Easy || 0} Easy · {breakdown.Medium || 0} Medium · {breakdown.Hard || 0} Hard
        </div>
      </div>

      <div style={{ minWidth: 180, flex: '1 1 220px', maxWidth: 320 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>
          <span>Overall</span><span>{pct}%</span>
        </div>
        <div style={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.15)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, borderRadius: 999, background: `linear-gradient(90deg, ${V2}, #fff)`, transition: 'width 0.4s ease' }} />
        </div>
      </div>
    </div>
  );
};

/* ─── Filter Bar ──────────────────────────────────────────── */
const FilterBar = ({ difficulty, setDifficulty, search, setSearch, solvedOnly, setSolvedOnly }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    background: 'var(--card-solid)', border: '1px solid var(--border)',
    borderRadius: 14, padding: '12px 16px',
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 200px',
      background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '9px 14px',
    }}>
      <SearchIcon />
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search problems…"
        style={{ border: 'none', outline: 'none', background: 'transparent', color: 'var(--text)', fontSize: 14, width: '100%', fontFamily: 'inherit' }}
      />
    </div>

    <div style={{ display: 'flex', gap: 6 }}>
      {DIFFICULTY_FILTERS.map(d => (
        <button
          key={d}
          onClick={() => setDifficulty(d)}
          style={{
            padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            border: `1.5px solid ${difficulty === d ? V1 : 'var(--border)'}`,
            background: difficulty === d ? V1 : 'transparent',
            color: difficulty === d ? '#fff' : 'var(--muted)',
            fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.15s',
          }}
        >
          {d}
        </button>
      ))}
    </div>

    <button
      onClick={() => setSolvedOnly(!solvedOnly)}
      style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
        border: `1.5px solid ${solvedOnly ? '#2ECC71' : 'var(--border)'}`,
        background: solvedOnly ? 'rgba(46,204,113,0.1)' : 'transparent',
        color: solvedOnly ? '#2ECC71' : 'var(--muted)',
        fontFamily: "'Satoshi','DM Sans',sans-serif", transition: 'all 0.15s', whiteSpace: 'nowrap',
      }}
    >
      <span style={{
        width: 14, height: 14, borderRadius: 4, border: `1.5px solid ${solvedOnly ? '#2ECC71' : 'var(--border-strong)'}`,
        background: solvedOnly ? '#2ECC71' : 'transparent',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10,
      }}>{solvedOnly ? '✓' : ''}</span>
      Solved only
    </button>
  </div>
);

/* ─── Pattern Group Accordion ────────────────────────────── */
const PatternGroup = ({ group, index, isOpen, onToggle, hasExplanation, onOpenExplanation, problems, onOpenProblem, solvedProblems, onToggleSolved, togglingId }) => {
  const solvedInGroup = (problems || []).filter(p => solvedProblems.has(p.id)).length;
  const groupDone = solvedInGroup === group.count;
  return (
  <div style={{
    background: 'var(--card-solid)', border: '1px solid var(--border)',
    borderRadius: 16, overflow: 'hidden',
  }}>
    <button
      onClick={onToggle}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 20px', background: 'none', border: 'none', cursor: 'pointer',
        fontFamily: "'Satoshi','DM Sans',sans-serif", textAlign: 'left',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{
          width: 32, height: 32, borderRadius: 9, flexShrink: 0,
          background: `${V1}18`, color: V2, fontSize: 13, fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{index + 1}</span>
        <span style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text)' }}>{group.name}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{
          fontSize: 12, fontWeight: 700, color: groupDone ? '#2ECC71' : 'var(--muted)',
          background: groupDone ? 'rgba(46,204,113,0.12)' : 'var(--bg3)',
          border: `1px solid ${groupDone ? 'rgba(46,204,113,0.35)' : 'var(--border)'}`,
          padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap',
        }}>{groupDone ? '✓ ' : ''}{solvedInGroup} / {group.count}</span>
        <span style={{ color: 'var(--muted)' }}><ChevronIcon open={isOpen} /></span>
      </div>
    </button>

    {isOpen && (
      <div style={{
        padding: '4px 20px 22px', borderTop: '1px solid var(--border)',
        color: 'var(--muted)', fontSize: 13.5, lineHeight: 1.6,
      }}>
        {hasExplanation && (
          <button
            onClick={onOpenExplanation}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12,
              marginTop: 16, padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
              border: `1.5px solid ${V1}35`, background: `linear-gradient(135deg, ${V1}12, ${V2}0a)`,
              textAlign: 'left', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >
            <span style={{
              flexShrink: 0, width: 36, height: 36, borderRadius: 10,
              background: V1, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><BookIcon /></span>
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Explanation — learn the pattern first</span>
              <span style={{ display: 'block', fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>Concept, analogy, visual walkthrough, Java syntax &amp; a self-check</span>
            </span>
            <span style={{ color: V1, fontSize: 12.5, fontWeight: 800, whiteSpace: 'nowrap' }}>Open →</span>
          </button>
        )}
        {problems ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: hasExplanation ? 18 : 16 }}>
            {problems.map(p => {
              const isSolved = solvedProblems.has(p.id);
              const isToggling = togglingId === p.id;
              return (
                <div
                  key={p.id}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px', borderRadius: 11,
                    border: `1px solid ${isSolved ? 'rgba(46,204,113,0.3)' : 'var(--border)'}`,
                    background: isSolved ? 'rgba(46,204,113,0.06)' : 'var(--bg3)',
                  }}
                >
                  <button
                    onClick={() => onToggleSolved(p.id)}
                    disabled={isToggling}
                    aria-label={isSolved ? `Mark "${p.title}" as unsolved` : `Mark "${p.title}" as solved`}
                    style={{
                      flexShrink: 0, width: 26, height: 26, borderRadius: 7, fontSize: 13, fontWeight: 800,
                      border: `1.5px solid ${isSolved ? '#2ECC71' : 'var(--border-strong)'}`,
                      background: isSolved ? '#2ECC71' : 'transparent', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: isToggling ? 'wait' : 'pointer', opacity: isToggling ? 0.6 : 1,
                    }}
                  >{isSolved ? '✓' : ''}</button>
                  <button
                    onClick={() => onOpenProblem(p)}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 12, minWidth: 0,
                      padding: 0, border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                      fontFamily: "'Satoshi','DM Sans',sans-serif",
                    }}
                  >
                    <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>{p.title}</span>
                    <span style={{
                      flexShrink: 0, fontSize: 10.5, fontWeight: 800, padding: '3px 9px', borderRadius: 999,
                      background: `${DIFFICULTY_COLOR[p.difficulty] || V2}18`, color: DIFFICULTY_COLOR[p.difficulty] || V2,
                    }}>{p.difficulty}</span>
                    <span style={{ flexShrink: 0, color: V1, fontSize: 12, fontWeight: 800 }}>View →</span>
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ paddingTop: hasExplanation ? 18 : 16 }}>
            Problems for this pattern are being finalized — check back soon.
          </div>
        )}
      </div>
    )}
  </div>
  );
};

/* ─── Certificate Section ────────────────────────────────── */
const CertificateSection = ({ solved, total, certificateId, onDownload, downloading }) => {
  const unlocked = !!certificateId;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
      background: unlocked ? 'linear-gradient(135deg, rgba(46,204,113,0.12), rgba(46,204,113,0.04))' : 'var(--bg3)',
      border: `1.5px solid ${unlocked ? 'rgba(46,204,113,0.4)' : 'var(--border-strong)'}`,
      borderRadius: 18, padding: '26px 28px',
    }}>
      <span style={{
        flexShrink: 0, width: 52, height: 52, borderRadius: 14,
        background: unlocked ? '#2ECC71' : 'var(--bg2)', color: unlocked ? '#fff' : 'var(--muted)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{unlocked ? <CertificateIcon /> : <LockIcon />}</span>

      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 17, fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>
          {unlocked ? 'Java DSA Mastery Certificate — Unlocked 🎉' : 'Java DSA Mastery Certificate'}
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.6 }}>
          {unlocked
            ? `Congratulations — you've solved all ${total} problems. Your certificate is ready to download.`
            : `Solve all ${total} problems to unlock your certificate. ${solved} / ${total} solved so far.`}
        </div>
      </div>

      {unlocked ? (
        <button
          onClick={onDownload}
          disabled={downloading}
          style={{
            flexShrink: 0, padding: '13px 26px', borderRadius: 999, border: 'none',
            background: '#2ECC71', color: '#fff', fontSize: 14, fontWeight: 800,
            cursor: downloading ? 'wait' : 'pointer', opacity: downloading ? 0.7 : 1,
            fontFamily: "'Satoshi','DM Sans',sans-serif", whiteSpace: 'nowrap',
          }}
        >{downloading ? 'Preparing…' : 'Download Certificate ↓'}</button>
      ) : (
        <span style={{
          flexShrink: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--muted)',
          background: 'var(--bg2)', border: '1px solid var(--border)',
          padding: '8px 16px', borderRadius: 999, whiteSpace: 'nowrap',
        }}>{total - solved} to go</span>
      )}
    </div>
  );
};

/* ─── Main Tracker Shell ─────────────────────────────────── */
const DsaTracker = ({ enrollmentId, email, initialSolvedProblems = [], initialCertificateId = null }) => {
  const [openIndex, setOpenIndex] = useState(0);
  const [difficulty, setDifficulty] = useState('All');
  const [search, setSearch] = useState('');
  const [solvedOnly, setSolvedOnly] = useState(false);
  const [solvedProblems, setSolvedProblems] = useState(() => new Set(initialSolvedProblems));
  const [certificateId, setCertificateId] = useState(initialCertificateId);
  const [togglingId, setTogglingId] = useState(null);
  const [downloadingCert, setDownloadingCert] = useState(false);

  const toggleSolved = async (problemId) => {
    if (!enrollmentId || togglingId) return;
    setTogglingId(problemId);
    try {
      const res = await API.put(`/dsa/${enrollmentId}/toggle-solved`, { email, problemId });
      setSolvedProblems(new Set(res.data.solvedProblems));
      setCertificateId(res.data.certificateId || null);
    } catch (err) {
      console.error('Failed to update solved status:', err);
    } finally {
      setTogglingId(null);
    }
  };

  const downloadCertificate = async () => {
    if (!enrollmentId || !certificateId) return;
    setDownloadingCert(true);
    try {
      const res = await API.get(`/dsa/${enrollmentId}/certificate`, { params: { email } });
      await downloadFile(res.data.certificateUrl, `Java_DSA_Certificate_${res.data.certificateId}.pdf`);
    } catch (err) {
      console.error('Failed to download certificate:', err);
    } finally {
      setDownloadingCert(false);
    }
  };

  const solvedCount = solvedProblems.size;
  const difficultyBreakdown = ALL_PROBLEMS.reduce((acc, p) => {
    if (solvedProblems.has(p.id)) acc[p.difficulty] = (acc[p.difficulty] || 0) + 1;
    return acc;
  }, {});
  const [explanationPattern, setExplanationPattern] = useState(null);
  const [activeProblem, setActiveProblem] = useState(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, textAlign: 'left' }}>
      <ProgressHero solved={solvedCount} total={TOTAL_PROBLEMS} breakdown={difficultyBreakdown} />

      <FilterBar
        difficulty={difficulty} setDifficulty={setDifficulty}
        search={search} setSearch={setSearch}
        solvedOnly={solvedOnly} setSolvedOnly={setSolvedOnly}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {PATTERN_GROUPS.map((group, i) => (
          <PatternGroup
            key={group.name}
            group={group}
            index={i}
            isOpen={openIndex === i}
            onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            hasExplanation={!!EXPLANATIONS[group.name]}
            onOpenExplanation={() => setExplanationPattern(group.name)}
            problems={PROBLEMS[group.name]}
            onOpenProblem={setActiveProblem}
            solvedProblems={solvedProblems}
            onToggleSolved={toggleSolved}
            togglingId={togglingId}
          />
        ))}
      </div>

      <CertificateSection
        solved={solvedCount}
        total={TOTAL_PROBLEMS}
        certificateId={certificateId}
        onDownload={downloadCertificate}
        downloading={downloadingCert}
      />

      {explanationPattern && (
        <DsaExplanationModal
          content={EXPLANATIONS[explanationPattern]}
          onClose={() => setExplanationPattern(null)}
        />
      )}

      {activeProblem && (
        <DsaProblemModal
          problem={activeProblem}
          onClose={() => setActiveProblem(null)}
        />
      )}
    </div>
  );
};

export default DsaTracker;
